import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendMessageToAI } from '../../pages/dashboard/services/dashboardAiService';
import { parseDualResponse, textToSpeech, transcribeAudio, getVoiceLanguage, setVoiceLanguage as persistVoiceLanguage, processVoiceTranscription, buildRetryQueue, setRetryQueue, isVoiceOutputEnabled } from '../../pages/events-explorer/services/openaiVoiceService';
import usePushToTalk from '../../pages/events-explorer/hooks/usePushToTalk';
import { store } from '../../providers/storeProvider/config/store.ts';

const VOICE_PREF_KEY = 'ai_voice_enabled';

const getStoredVoicePref = () => {
  try {
    const stored = localStorage.getItem(VOICE_PREF_KEY);
    return stored === null ? true : stored === 'true';
  } catch { return true; }
};

/**
 * Hook for Global AI voice search.
 * Local state only — no Redux dependency.
 */
const useGlobalAIHelper = () => {
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [voiceEnabled, setVoiceEnabledRaw] = useState(getStoredVoicePref);
  const [voiceLanguage, setVoiceLanguageState] = useState(getVoiceLanguage);

  const setVoiceEnabled = useCallback((value) => {
    setVoiceEnabledRaw(value);
    try { localStorage.setItem(VOICE_PREF_KEY, String(value)); } catch {}
  }, []);

  const handleLanguageChange = useCallback((code) => {
    persistVoiceLanguage(code);
    setVoiceLanguageState(code);
  }, []);

  const conversationHistory = useRef([]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    conversationHistory.current = [];
  }, []);

  const toggleHelper = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const sendMessage = useCallback(async (userMessage, { skipUserBubble = false } = {}) => {
    if (!userMessage.trim() || isProcessing) return;

    // Add user message (skip when voice already added it)
    if (!skipUserBubble) {
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    }

    // Add loading placeholder
    setIsProcessing(true);
    setMessages(prev => [...prev, { role: 'assistant', isLoading: true }]);

    try {
      // Read the voice toggle fresh from its persisted source of truth (not the
      // captured `voiceEnabled` state) so a "voice off" set here or in another
      // panel always suppresses speech, even against a stale closure/state.
      const voiceOn = isVoiceOutputEnabled();

      const result = await sendMessageToAI(
        userMessage,
        conversationHistory.current,
        navigate,
        (status) => setCurrentStatus(status),
        voiceOn
      );

      conversationHistory.current = result.messages;

      let displayText = result.text;
      if (voiceOn) {
        const { screenText, voiceText } = parseDualResponse(result.text);
        displayText = screenText;
        if (voiceText) {
          textToSpeech(voiceText).then(blob => {
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audio.onended = () => URL.revokeObjectURL(url);
            audio.play().catch(console.error);
          }).catch(console.error);
        }
      }

      setCurrentStatus(null);
      setIsProcessing(false);
      // Replace loading message with actual response
      setMessages(prev => {
        const updated = [...prev];
        const loadingIdx = updated.findLastIndex(m => m.isLoading);
        if (loadingIdx !== -1) {
          updated[loadingIdx] = {
            role: 'assistant',
            content: displayText,
            actions: result.actions.length > 0 ? result.actions : undefined
          };
        }
        return updated;
      });

    } catch (error) {
      console.error('Global AI Helper Error:', error);
      setCurrentStatus(null);
      setIsProcessing(false);
      setMessages(prev => {
        const updated = [...prev];
        const loadingIdx = updated.findLastIndex(m => m.isLoading);
        if (loadingIdx !== -1) {
          updated[loadingIdx] = {
            role: 'assistant',
            content: getErrorMessage(error),
            error: true
          };
        }
        return updated;
      });
    }
  }, [isProcessing, navigate, voiceEnabled]);

  // Voice: transcribe audio and feed into sendMessage
  const handleRecordingComplete = useCallback(async (blob) => {
    try {
      setIsOpen(true);
      setMessages(prev => [...prev, { role: 'user', content: '🎤 Transcribing...', isTranscribing: true }]);
      // Gather player names from available Redux state for Whisper prompt
      const state = store.getState();
      const contextPlayerNames = [];
      const teamPlayers = state.team?.teamStatistic?.players_list;
      if (Array.isArray(teamPlayers)) {
        teamPlayers.forEach(p => { const n = p?.display_name || p?.name; if (n) contextPlayerNames.push(n); });
      }
      const matchPlayers = state.match?.matchPlayers;
      if (Array.isArray(matchPlayers)) {
        matchPlayers.forEach(p => { const n = p?.display_name || p?.name; if (n) contextPlayerNames.push(n); });
      }

      const rawText = await transcribeAudio(blob, voiceLanguage, contextPlayerNames);
      // Run name correction pipeline (Modes A/B/C)
      const correctedText = await processVoiceTranscription(rawText);
      // Set retry queue for fallback search
      setRetryQueue(buildRetryQueue(rawText, correctedText));
      // Replace transcribing placeholder with actual text
      setMessages(prev => {
        const updated = [...prev];
        const idx = updated.findLastIndex(m => m.isTranscribing);
        if (idx !== -1) {
          updated[idx] = { role: 'user', content: correctedText };
        }
        return updated;
      });
      // Send the corrected text (user bubble already shown above)
      await sendMessage(correctedText, { skipUserBubble: true });
    } catch (error) {
      console.error('Transcription error:', error);
      setMessages(prev => {
        const updated = [...prev];
        const idx = updated.findLastIndex(m => m.isTranscribing);
        if (idx !== -1) {
          updated[idx] = { role: 'assistant', content: 'Failed to transcribe audio. Please try again.', error: true };
        }
        return updated;
      });
    }
  }, [sendMessage, voiceLanguage]);

  const { isRecording } = usePushToTalk({
    enabled: voiceEnabled && !isProcessing,
    onRecordingComplete: handleRecordingComplete
  });

  return {
    isOpen,
    setIsOpen,
    toggleHelper,
    messages,
    isProcessing,
    currentStatus,
    sendMessage,
    clearMessages,
    isRecording,
    voiceEnabled,
    setVoiceEnabled,
    voiceLanguage,
    handleLanguageChange
  };
};

const getErrorMessage = (error) => {
  const message = error.message || 'An error occurred';
  if (message.includes('API key')) {
    return "AI assistant is not configured yet. The API key may be missing.";
  }
  if (message.includes('network') || message.includes('fetch')) {
    return "Connection error. Please check your internet connection and try again.";
  }
  if (message.includes('rate limit') || message.includes('429')) {
    return "Too many requests. Please wait a moment and try again.";
  }
  return `Sorry, something went wrong: ${message}`;
};

export default useGlobalAIHelper;
